import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createColumnHelper, useReactTable, getCoreRowModel, flexRender, type SortingState } from '@tanstack/react-table';
import { Plus, Search, Link2, Loader2 } from 'lucide-react';
import { useInfiniteOrders } from '../../hooks/useOrders';
import { useUrnResolver } from '../../hooks/useUrnResolver';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import { formatDateTime } from '../../utils/date';
import type { Order } from '../../api/orders';

const ORDER_STATUSES = [
  'all',
  'created',
  'queued',
  'processing',
  'input_required',
  'pending',
  'completed',
  'cancel.submitted',
  'canceling',
  'canceled',
  'declined',
  'withdrawn',
  'failed',
  'error',
];

const columnHelper = createColumnHelper<Order>();

export default function OrdersListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [cascadeOnly, setCascadeOnly] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'createdAt', desc: true },
  ]);
  const { resolveProductUrn, resolveLenderUrn, resolveUserUrn } = useUrnResolver();

  const queryParams = useMemo(() => ({
    sortField: sorting[0]?.id === 'createdAt' ? 'creationDate' : (sorting[0]?.id || 'creationDate'),
    sortOrder: (sorting[0]?.desc ? 'desc' : 'asc') as 'asc' | 'desc',
    ...(search ? { id: search } : {}),
    ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    ...(cascadeOnly ? { cascade: 'true' } : {}),
  }), [sorting, search, statusFilter, cascadeOnly]);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteOrders(queryParams);

  const allOrders = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  const totalCount = data?.pages[0]?.total;

  // Intersection observer for infinite scroll
  const sentinelRef = useRef<HTMLDivElement>(null);

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(handleIntersect, {
      rootMargin: '200px',
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleIntersect]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('urn', {
        header: 'URN',
        cell: (info) => (
          <span className="font-mono text-xs text-blue-600 dark:text-blue-400">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('product', {
        header: 'Product',
        cell: (info) => (
          <span className="text-sm truncate max-w-[180px] block dark:text-gray-200" title={info.getValue()}>
            {resolveProductUrn(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => {
          const order = info.row.original;
          const isFailed = info.getValue() === 'failed' || info.getValue() === 'error';
          return (
            <StatusBadge
              status={info.getValue()}
              tooltip={isFailed && order.message ? order.message : undefined}
            />
          );
        },
      }),
      columnHelper.display({
        id: 'origin',
        header: 'Origin',
        cell: (info) => {
          const order = info.row.original;
          if (order.createdByRule || order.parent) {
            return (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" title={order.createdByRule ? `Rule: ${order.createdByRule}` : 'Cascade order'}>
                <Link2 className="h-3 w-3" />
                {order.createdByRule || 'cascade'}
              </span>
            );
          }
          return <span className="text-xs text-gray-400 dark:text-gray-500">direct</span>;
        },
      }),
      columnHelper.accessor('originator', {
        header: 'Lender',
        cell: (info) => (
          <span className="text-sm truncate max-w-[150px] block dark:text-gray-200" title={info.getValue()}>
            {resolveLenderUrn(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor('requester', {
        header: 'Requester',
        cell: (info) => (
          <span className="text-sm truncate max-w-[150px] block dark:text-gray-200" title={info.getValue()}>
            {resolveUserUrn(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor('createdAt', {
        header: 'Created',
        cell: (info) => (
          <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
            {formatDateTime(info.getValue())}
          </span>
        ),
      }),
      columnHelper.display({
        id: 'updated',
        header: 'Updated',
        cell: (info) => {
          const order = info.row.original;
          const updatedDate = order.modificationDate
            || (order.events?.length ? order.events[order.events.length - 1].date : null);
          return (
            <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {formatDateTime(updatedDate)}
            </span>
          );
        },
      }),
    ],
    [resolveProductUrn, resolveLenderUrn, resolveUserUrn],
  );

  const table = useReactTable({
    data: allOrders,
    columns,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(next);
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
  });

  return (
    <div>
      <PageHeader
        title="Orders"
        description="Manage and monitor all OMS orders"
        actions={
          <button onClick={() => navigate('/orders/new')} className="btn-primary gap-1">
            <Plus className="h-4 w-4" />
            New Order
          </button>
        }
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by order ID or URN..."
            className="input pl-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="input w-auto"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {ORDER_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s === 'all' ? 'All Statuses' : s.replace(/[._]/g, ' ')}
            </option>
          ))}
        </select>
        <button
          onClick={() => setCascadeOnly((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded border text-sm font-medium transition-colors ${
            cascadeOnly
              ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/40 dark:border-purple-700 dark:text-purple-300'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
          }`}
        >
          <Link2 className="h-4 w-4" />
          Cascade Only
        </button>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-3 py-2 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center gap-1">
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() === 'asc' && ' ↑'}
                        {header.column.getIsSorted() === 'desc' && ' ↓'}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="px-3 py-10 text-center text-gray-400 dark:text-gray-500">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto" />
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-3 py-10 text-center text-gray-400 dark:text-gray-500">
                    No orders found
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => {
                      const order = row.original;
                      navigate(`/orders/${order.uuid || order.urn}`);
                    }}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-2 whitespace-nowrap">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer with count + infinite scroll sentinel */}
        <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Showing {allOrders.length}{totalCount != null ? ` of ${totalCount}` : ''} orders
          </p>
          {isFetchingNextPage && (
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading more...
            </div>
          )}
        </div>
      </div>

      {/* Intersection observer sentinel for infinite scroll */}
      <div ref={sentinelRef} className="h-1" />
    </div>
  );
}
